// src/pages/Community/WeponsVehicleIndex.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams, useOutletContext } from "react-router-dom";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";
import "./css/ui-shell.css";
import "./css/WeponsVehicleIndex.css";
import ToolbarHeader, { GTA_GAME_TABS } from "./components/ToolbarHeader";
import Modal from "./components/Modal";
import WeponsVehicleForm from "../Profile/WeponsVehicleForm";

/* ----------------- utils ----------------- */
const canonGameKey = (text) => {
  const s = (text || "").toLowerCase();
  if (/san\s*andreas/.test(s)) return "sa";
  if (/vice\s*city/.test(s)) return "vc";
  if (/gta[\s:_\-\s]*(vi|6)\b/.test(s) || /^\s*(vi|6)\s*$/.test(s)) return "gta-6";
  if (/gta[\s:_\-\s]*(v|5)\b/.test(s) || /^\s*(v|5)\s*$/.test(s)) return "gta-5";
  if (/gta[\s:_\-\s]*(iv|4)\b/.test(s) || /^\s*(iv|4)\s*$/.test(s)) return "gta-4";
  if (/gta[\s:_\-\s]*(iii|3)\b/.test(s) || /^\s*(iii|3)\s*$/.test(s)) return "gta-3";
  return null;
};
const num = (n) => Number(n).toLocaleString("en-US");
const isNum = (v) => typeof v === "number" && Number.isFinite(v);

/* ----------------- component ----------------- */
export default function WeponsVehicleIndex({ user: propUser }) {
  // call hook directly; provide safe default
  const outlet = useOutletContext() || {};
  const user = propUser ?? outlet.user;

  const [vehicles, setVehicles] = useState([]);
  const [weapons, setWeapons] = useState([]);
  const [loadingVeh, setLoadingVeh] = useState(true);
  const [loadingWeap, setLoadingWeap] = useState(true);

  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "vehicles"); // vehicles | weapons
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [selectedKey, setSelectedKey] = useState(searchParams.get("game") || "all"); // game filter

  const [showModal, setShowModal] = useState(false);

  // keep last written params to avoid effect loop / over-writing the URL
  const lastParamsRef = useRef({
    tab: searchParams.get("tab") || "",
    q: searchParams.get("q") || "",
    game: searchParams.get("game") || "",
  });

  /* ------------- helper: attach collection listener ------------- */
  // small helper to avoid copy/paste between vehicles/weapons
  const attachCollectionListener = (opts) => {
    const {
      collectionName,
      selectedKey,
      setList,
      setLoading,
      mapDoc,
      userUid,
    } = opts;

    let alive = true;
    let unsub = null;
    const gen = Math.random();
    const base = collection(db, collectionName);

    const attach = (ref, generation) =>
      onSnapshot(
        ref,
        (snap) => {
          if (!alive || generation !== gen) return;
          const rows = snap.docs.map((d) => mapDoc(d.id, d.data()));
          setList(rows);
          setLoading(false);
        },
        (err) => {
          if (!alive || generation !== gen) return;
          setLoading(false);

          const msg = String(err?.message || "");
          const needsFallback =
            selectedKey !== "mine" &&
            (msg.includes("FAILED_PRECONDITION") ||
              msg.includes("failed-precondition"));

          // retry WITHOUT orderBy to avoid needing composite index
          if (needsFallback) {
            try {
              if (unsub) {
                try {
                  unsub();
                } catch {}
                unsub = null;
              }
              const fallbackRef =
                selectedKey === "all"
                  ? base // no orderBy
                  : query(base, where("gameKey", "==", selectedKey));
              unsub = attach(fallbackRef, generation);
            } catch (e) {
              console.error(`${collectionName} fallback failed:`, e);
            }
            return;
          }

          console.error(`${collectionName} query error:`, err);
        }
      );

    setLoading(true);
    setList([]);

    let firstRef;
    if (selectedKey === "all") {
      firstRef = query(base, orderBy("createdAt", "desc"));
    } else if (selectedKey === "mine") {
      firstRef = userUid
        ? query(base, where("authorUid", "==", userUid))
        : query(base, where("authorUid", "==", "__none__"));
    } else {
      firstRef = query(
        base,
        where("gameKey", "==", selectedKey),
        orderBy("createdAt", "desc")
      );
    }

    unsub = attach(firstRef, gen);

    return () => {
      alive = false;
      if (unsub) {
        try {
          unsub();
        } catch {}
      }
    };
  };

  /* ----------------- Firestore: VEHICLES ----------------- */
  useEffect(() => {
    return attachCollectionListener({
      collectionName: "vehicles",
      selectedKey,
      setList: setVehicles,
      setLoading: setLoadingVeh,
      mapDoc: mapVehicleDoc,
      userUid: user?.uid || null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey, user?.uid]);

  /* ----------------- Firestore: WEAPONS ----------------- */
  useEffect(() => {
    return attachCollectionListener({
      collectionName: "weapons",
      selectedKey,
      setList: setWeapons,
      setLoading: setLoadingWeap,
      mapDoc: mapWeaponDoc,
      userUid: user?.uid || null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey, user?.uid]);

  /* ----------------- URL sync (?tab, ?q, ?game) ----------------- */
  useEffect(() => {
    const next = {};
    if (tab) next.tab = tab;
    if (search) next.q = search;
    if (selectedKey && selectedKey !== "all") next.game = selectedKey;

    const prev = lastParamsRef.current;

    const changed =
      (next.tab || "") !== prev.tab ||
      (next.q || "") !== prev.q ||
      (next.game || "") !== prev.game;

    if (changed) {
      setSearchParams(next, { replace: true });
      lastParamsRef.current = {
        tab: next.tab || "",
        q: next.q || "",
        game: next.game || "",
      };
    }
    // depend on the app state only; writing sets lastParamsRef so we don't loop.
  }, [tab, search, selectedKey, setSearchParams]);

  /* ----------------- Derived list ----------------- */
  const isLoading = tab === "weapons" ? loadingWeap : loadingVeh;

  const list = useMemo(() => {
    const src = tab === "weapons" ? weapons : vehicles;
    let arr = [...src];

    // Hide private items for everyone except the owner (unless "mine")
    if (selectedKey !== "mine") {
      arr = arr.filter((x) => {
        const ownerUid = x.authorUid ?? null;
        const isOwner = !!user?.uid && ownerUid === user.uid;
        const isPrivate = Boolean(x.isPrivate ?? x.isHidden);
        return !isPrivate || isOwner;
      });
    }

    if (selectedKey === "mine" && user?.uid) {
      arr = arr.filter((x) => x.authorUid === user.uid);
    }
    if (selectedKey !== "all" && selectedKey !== "mine") {
      arr = arr.filter((x) => {
        if (x.gameKey) return x.gameKey === selectedKey;
        const k = canonGameKey(x.game);
        return k === selectedKey;
      });
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter((x) =>
        [
          x.name,
          ...(x.tags || []),
          x.make || x.manufacturer || "",
          x.class || x.type || "",
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q)
      );
    }

    // Sort newest when broad, alpha when specific for grid stability
    if (selectedKey === "all" || selectedKey === "mine") {
      arr.sort(
        (a, b) =>
          (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)
      );
    } else {
      arr.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }

    return arr;
  }, [tab, weapons, vehicles, search, selectedKey, user?.uid]);

  /* ----------------- render ----------------- */
  return (
    <>
      <div className="wepo-wrap">  
        <ToolbarHeader
          children={
            <div
              className="forum-tabs vw-inline-tabs"
              role="tablist"
              aria-label="Game & Asset filters"
            >
              {/* Game tabs */}
              {GTA_GAME_TABS.map(({ key, label }) => {
                const active = selectedKey === key;
                return (
                  <span
                    key={key}
                    className={`tab-text ${active ? "active" : ""}`}
                    onClick={() => setSelectedKey(key)}
                    role="tab"
                    aria-selected={active}
                  >
                    {label}
                  </span>
                );
              })}
              {user && (
                <span
                  className={`tab-text ${selectedKey === "mine" ? "active" : ""}`}
                  onClick={() => setSelectedKey("mine")}
                  role="tab"
                  aria-selected={selectedKey === "mine"}
                >
                  My Assets
                </span>
              )}

              {/* divider */}
              <span className="tab-divider" aria-hidden="true" />

              {/* Asset filter as tabs (shares the same row) */}
              <span
                className={`tab-text vw-asset-tab ${tab === "vehicles" ? "active" : ""}`}
                onClick={() => setTab("vehicles")}
                role="tab"
                aria-selected={tab === "vehicles"}
                title="Vehicles"
              >
                🚗 Vehicles <span className="tab-chip">{vehicles.length}</span>
              </span>
              <span
                className={`tab-text vw-asset-tab ${tab === "weapons" ? "active" : ""}`}
                onClick={() => setTab("weapons")}
                role="tab"
                aria-selected={tab === "weapons"}
                title="Weapons"
              >
                🔫 Weapons <span className="tab-chip">{weapons.length}</span>
              </span>
            </div>
          }
          search={search}
          setSearch={setSearch}
          placeholder={`Search ${tab === "weapons" ? "weapons" : "vehicles"}…`}
          user={user}
          onAddCharacter={() => setShowModal(true)}
          addCharacterLabel="＋ Add Asset"
        />

        {/* Gallery */}
        <ul className="card-grid sc-gallery vw-gallery">
          {isLoading &&
            Array.from({ length: 8 }).map((_, i) => (
              <li key={`sk-${i}`} className="card sc-card vw-card">
                <div className="sc-thumb vw-thumb skeleton" />
                <div className="sc-name skeleton skeleton-text" />
                <div className="sc-subtle vw-subline">
                  <span className="chip skeleton skeleton-chip" />
                  <span className="chip skeleton skeleton-chip" />
                </div>
              </li>
            ))}

          {!isLoading &&
            list.map((item) => {
              const isPrivate = Boolean(item.isPrivate ?? item.isHidden);
              const visLabel = isPrivate ? "Private" : "Public";
              const visClass = isPrivate ? "is-private is-hidden" : "is-public is-visible";

              const ownerUid = item.authorUid ?? null;
              const isOwner = !!user?.uid && ownerUid === user.uid;

              const gameLabel =
                GTA_GAME_TABS.find((g) => g.key === item.gameKey)?.label ||
                (item.gameKey ? item.gameKey : "Game");

              return (
                <li key={item.id} className="card sc-card vw-card">
                  {/* owner crown */}
                  {isOwner && (
                    <span className="vw-owner" title="Your asset" aria-label="Your asset">
                      👑
                    </span>
                  )}

                  <Link
                    to={
                      tab === "weapons"
                        ? `/community/weapons/${item.id}`
                        : `/community/vehicles/${item.id}`
                    }
                    className="card-link sc-link"
                  >
                    <div className="sc-thumb vw-thumb">
                      {item.image ? (
                        <img
                          className="sc-img vw-img"
                          src={item.image}
                          alt={`${item.name} ${tab === "weapons" ? "weapon" : "vehicle"}`}
                          loading="lazy"
                        />
                      ) : (
                        <div className="sc-img sc-img--placeholder vw-placeholder" aria-label="No image">
                          {tab === "weapons" ? "🔫" : "🚗"}
                        </div>
                      )}

                      {/* tags over image */}
                      {Array.isArray(item.tags) && item.tags.length > 0 && (
                        <div className="vw-tags">
                          {item.tags.slice(0, 2).map((t) => (
                            <span className="vw-tag" key={t}>
                              {t}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* top row like LocationIndex: game badge + visibility */}
                    <div className="vw-row">
                      <span className="vw-badge">{gameLabel}</span>
                      <span className={`vw-tag ${visClass}`}>{visLabel}</span>
                    </div>

                    <div className="sc-name vw-title" title={item.name}>
                      {item.name}
                    </div>

                    <div className="sc-subtle vw-subline">
                      {tab === "weapons" ? (
                        <>
                          {item.type && <span className="chip">{item.type}</span>}
                          {item.manufacturer && <span className="chip">{item.manufacturer}</span>}
                          {isNum(item.price) && <span className="chip">${num(item.price)}</span>}
                        </>
                      ) : (
                        <>
                          {item.make && <span className="chip">{item.make}</span>}
                          {item.class && <span className="chip">{item.class}</span>}
                          {isNum(item.price) && <span className="chip">${num(item.price)}</span>}
                        </>
                      )}
                    </div>
                  </Link>
                </li>
              );
            })}

          {!isLoading && list.length === 0 && (
            <div className="empty">No {tab} match your filters.</div>
          )}
        </ul>

        {/* Add Asset Modal */}
        <Modal
          open={showModal}
          onClose={() => setShowModal(false)}
          title="Add Asset (Weapon / Vehicle)"
          size="lg"
          closeOnBackdrop
          showClose
        >
          <WeponsVehicleForm
            user={user}
            onDone={() => setShowModal(false)}
            onCancel={() => setShowModal(false)}
            defaultGameKey={
              selectedKey && selectedKey !== "all" && selectedKey !== "mine"
                ? selectedKey
                : "gta-6"
            }
          />
        </Modal>
      </div>
    </>
  );
}

/* ----------------- mappers ----------------- */
function mapVehicleDoc(id, x = {}) {
  const image =
    x.coverUrl ||
    x.coverImage ||
    (Array.isArray(x.gallery) && x.gallery.length > 0 ? x.gallery[0] : "") ||
    x.image ||
    x.imageUrl ||
    "";

  return {
    id,
    name: x.name || x.vehicleName || "—",
    image,
    tags: Array.isArray(x.tags) ? x.tags : [],
    make: x.make || x.manufacturer || "",
    class: x.class || "",
    price: isNum(x.price) ? x.price : undefined,
    createdAt: x.createdAt,
    authorUid: x.authorUid || x.createdBy || null,
    gameKey: x.gameKey || canonGameKey(x.game) || null,
    game: x.game || "",
    // visibility (back-compat)
    isPrivate: Boolean(x.isPrivate ?? x.isHidden ?? false),
  };
}

function mapWeaponDoc(id, x = {}) {
  const image =
    x.coverUrl ||
    x.coverImage ||
    (Array.isArray(x.gallery) && x.gallery.length > 0 ? x.gallery[0] : "") ||
    x.image ||
    x.imageUrl ||
    x.portrait ||
    "";

  return {
    id,
    name: x.name || "—",
    image,
    tags: Array.isArray(x.tags) ? x.tags : [],
    type: x.type || x.category || "",
    manufacturer: x.manufacturer || "",
    price: isNum(x.price) ? x.price : undefined,
    createdAt: x.createdAt,
    authorUid: x.authorUid || x.createdBy || null,
    gameKey: x.gameKey || canonGameKey(x.game) || null,
    game: x.game || "",
    // visibility (back-compat)
    isPrivate: Boolean(x.isPrivate ?? x.isHidden ?? false),
  };
}
